import {
  useInitializeAssetTemplateFilter,
  useInitializeAssetTemplatePicker,
  useInitializeAssetTemplateUnavailable,
} from '../hooks';
import type {
  IAssetTemplateFilterResult,
  IAssetTemplateUnavailableResult,
  IAssetTemplatePickerResult,
} from '../hooks';
import type { IBaseCampaignGroup } from '~/modules/campaign-group';
import type { IBaseCampaign } from '~/modules/campaign/types';
import type { ReactNode } from 'react';

import { createContext, useContext, useMemo } from 'react';

interface AssetTemplatePickedContext
  extends IAssetTemplatePickerResult, IAssetTemplateFilterResult, IAssetTemplateUnavailableResult {}

interface AssetTemplatePickerProviderProps {
  children: ReactNode;
  campaigns: IBaseCampaign[];
  campaign?: IBaseCampaign;
  campaignGroup?: IBaseCampaignGroup;
  isOpenModalMultiple: boolean;
}

export const AssetTemplatePickerContext = createContext<AssetTemplatePickedContext | undefined>(
  undefined,
);

export const AssetTemplatePickerProvider: React.FC<AssetTemplatePickerProviderProps> = ({
  children,
  campaign,
  campaigns,
  campaignGroup,
  isOpenModalMultiple,
}) => {
  const initializePickTemplate = useInitializeAssetTemplatePicker({
    isOpenModalMultiple,
  });
  const initializeTemplateFilter = useInitializeAssetTemplateFilter();
  const initializeTemplateUnavailable = useInitializeAssetTemplateUnavailable({
    campaign,
    campaigns,
    campaignGroup,
  });

  const contextValue = useMemo(
    () => ({
      ...initializePickTemplate,
      ...initializeTemplateFilter,
      ...initializeTemplateUnavailable,
    }),
    [initializePickTemplate, initializeTemplateFilter, initializeTemplateUnavailable],
  );

  return (
    <AssetTemplatePickerContext.Provider value={contextValue}>
      {children}
    </AssetTemplatePickerContext.Provider>
  );
};

export const useAssetTemplatePickerContext = () => {
  const context = useContext(AssetTemplatePickerContext);
  if (!context) {
    throw new Error(
      'useAssetTemplatePickerContext must be used within a AssetTemplatePickerProvider',
    );
  }
  return context;
};
